import { useState, useEffect } from 'react'
import { supabase } from './supabaseClient'

export default function BlogList({ onNavigate, addToast }) {
  const [blogs, setBlogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [deleting, setDeleting] = useState(null)

  useEffect(() => { loadBlogs() }, [])

  async function loadBlogs() {
    setLoading(true)
    const { data } = await supabase.from('blogs').select('id,title,slug,status,ai_generated,seo_score,focus_keyword,created_at,published_at,content_type,is_pillar').order('created_at', { ascending: false })
    setBlogs(data || [])
    setLoading(false)
  }

  async function deleteBlog(id) {
    setDeleting(id)
    const blog = blogs.find(b => b.id === id)
    const { error } = await supabase.from('blogs').delete().eq('id', id)
    if (error) { addToast('Failed to delete blog', 'error') }
    else {
      addToast('Blog deleted')
      setBlogs(prev => prev.filter(b => b.id !== id))
      // Reset keyword so n8n can regenerate it
      if (blog?.focus_keyword) {
        supabase.from('keywords').update({ status: 'pending' }).eq('keyword', blog.focus_keyword).then(() => {})
      }
    }
    setDeleting(null)
  }

  async function togglePublish(blog) {
    const newStatus = blog.status === 'published' ? 'draft' : 'published'
    const update = { status: newStatus }
    if (newStatus === 'published') update.published_at = new Date().toISOString()
    const { error } = await supabase.from('blogs').update(update).eq('id', blog.id)
    if (error) {
      addToast('Failed to update status', 'error')
    } else {
      addToast(newStatus === 'published' ? 'Blog published! 🎉' : 'Moved to draft')
      setBlogs(prev => prev.map(b => b.id === blog.id ? { ...b, ...update } : b))
      // Sync keyword status — non-blocking
      if (newStatus === 'published' && blog.focus_keyword) {
        supabase.from('keywords').update({ status: 'published' }).eq('keyword', blog.focus_keyword).then(() => {})
      } else if (newStatus === 'draft' && blog.focus_keyword) {
        supabase.from('keywords').update({ status: 'in_progress' }).eq('keyword', blog.focus_keyword).then(() => {})
      }
      // Fire publish hooks when publishing — same as editor
      if (newStatus === 'published') {
        supabase.auth.getSession().then(({ data: { session } }) => {
          fetch('/api/publish-hooks', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session?.access_token}`,
            },
            body: JSON.stringify({ slug: blog.slug, title: blog.title }),
          })
          .then(r => r.json())
          .then(d => console.log('Publish hooks:', d.results))
          .catch(e => console.error('Publish hooks failed:', e))
        })
      }
    }
  }

  const filtered = blogs.filter(b => {
    const matchFilter = filter === 'all' || b.status === filter
    const matchSearch = !search || b.title.toLowerCase().includes(search.toLowerCase()) || (b.focus_keyword || '').toLowerCase().includes(search.toLowerCase())
    return matchFilter && matchSearch
  })

  function formatDate(d) {
    if (!d) return '—'
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  }

  return (
    <div className="page-content">
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, alignItems: 'center' }}>
        <input
          className="form-input"
          style={{ maxWidth: 280 }}
          placeholder="Search blogs or keywords…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div style={{ display: 'flex', gap: 4 }}>
          {['all', 'published', 'draft', 'scheduled'].map(f => (
            <button key={f} className={`btn btn-sm ${filter === f ? 'btn-accent' : 'btn-ghost'}`} onClick={() => setFilter(f)}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        <button className="btn btn-accent btn-sm" style={{ marginLeft: 'auto' }} onClick={() => onNavigate('new')}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Blog
        </button>
      </div>

      <div className="data-table">
        <table>
          <thead>
            <tr>
              <th style={{ width: '38%' }}>Title</th>
              <th>Status</th>
              <th>Type</th>
              <th>SEO</th>
              <th>Keyword</th>
              <th>Source</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr className="loading-row"><td colSpan={8}><span className="spinner" /></td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: 'var(--text3)' }}>No blogs found</td></tr>
            )}
            {filtered.map(b => (
              <tr key={b.id}>
                <td className="td-primary td-link" onClick={() => onNavigate('edit', b.id)} style={{ maxWidth: 300 }}>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.title}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{b.slug}</div>
                </td>
                <td><span className={`badge badge-${b.status}`}>{b.status}</span></td>
                <td>
                  {b.is_pillar
                    ? <span style={{ background: '#287af6', color: '#fff', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>PILLAR</span>
                    : b.content_type === 'cluster'
                    ? <span style={{ background: 'rgba(61,200,255,0.15)', color: '#3dc8ff', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>CLUSTER</span>
                    : <span style={{ fontSize: 11, color: 'var(--text3)' }}>standalone</span>
                  }
                </td>
                <td>
                  <span style={{ color: b.seo_score >= 80 ? 'var(--green)' : b.seo_score >= 50 ? 'var(--amber)' : 'var(--red)', fontWeight: 500, fontSize: 13 }}>
                    {b.seo_score || 0}
                  </span>
                </td>
                <td style={{ fontSize: 12, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {b.focus_keyword || '—'}
                </td>
                <td>{b.ai_generated ? <span className="badge badge-ai">AI</span> : <span style={{ fontSize: 12, color: 'var(--text3)' }}>Manual</span>}</td>
                <td style={{ fontSize: 12 }}>{formatDate(b.published_at || b.created_at)}</td>
                <td>
                  <div className="row-actions">
                    <button className="btn btn-sm btn-ghost" onClick={() => onNavigate('edit', b.id)}>Edit</button>
                    <button className="btn btn-sm btn-ghost" onClick={() => togglePublish(b)}>
                      {b.status === 'published' ? 'Unpublish' : 'Publish'}
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => deleteBlog(b.id)}
                      disabled={deleting === b.id}
                    >
                      {deleting === b.id ? <span className="spinner" /> : 'Delete'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
